// src/infrastructure/integration/HttpIntegrationRepository.ts
import type { HttpClient } from '../http/HttpClient'
import type {
  AlanubeRegistrationDraft,
  ClaraCredentialsDraft,
  DianCredentialDraft,
  DianLegalRep,
  Integration,
  IntegrationRepository,
  SiiConnectInput,
  SiiEntity,
  TaxCredentialUpload,
  TestResult,
  TusdatosCredentialsDraft,
} from '@/domain/integration/Integration'
import { toIntegrations, toTest, type RawIntegration, type RawTest } from './integrationMapper'

export class HttpIntegrationRepository implements IntegrationRepository {
  constructor(private http: HttpClient) {}

  async list(): Promise<Integration[]> {
    return toIntegrations(await this.http.url('accountability/integrations').get<RawIntegration[]>())
  }

  async testSlack(): Promise<TestResult> {
    return toTest(await this.http.url('accountability/integrations/slack/test').post<RawTest>())
  }

  async testClara(societyId: number): Promise<TestResult> {
    return toTest(await this.http.url('accountability/integrations/clara/test').body({ society_id: societyId }).post<RawTest>())
  }

  async testSii(societyId: number): Promise<TestResult> {
    try {
      return toTest(await this.http.url('purchases/sii/credentials/test').body({ society_id: societyId }).post<RawTest>())
    } catch (error) {
      // The throttle answers 429 with the same `{ ok, error_code }` body.
      const response = (error as { response?: { status?: number, data?: RawTest } }).response
      if (response?.status === 429 && response.data) return toTest(response.data)
      throw error
    }
  }

  async testTaxCredential(credentialId: number): Promise<TestResult> {
    // This endpoint answers a free-text `message`, not a code.
    const raw = await this.http.url(`invoice-events/credentials/${credentialId}/test`).post<{ ok: boolean, message?: string }>()
    return { ok: raw.ok, errorCode: raw.ok ? null : 'auth_failed' }
  }

  async connectSlackUrl(): Promise<string> {
    return (await this.http.url('notifications/slack/integration').get<{ oauth_url: string }>()).oauth_url
  }

  async disconnectSlack(): Promise<void> {
    await this.http.url('notifications/slack/integration/disconnect').body({}).post()
  }

  async saveClara(d: ClaraCredentialsDraft): Promise<void> {
    await this.http.url('accountability/integrations/clara/credentials').body({
      society_id: d.societyId,
      client_id: d.clientId,
      client_secret: d.clientSecret,
      ca_cert: d.caCert,
      client_cert: d.clientCert,
      client_key: d.clientKey,
    }).post()
  }

  async disconnectClara(societyId: number): Promise<void> {
    await this.http.url('accountability/integrations/clara/credentials').params({ society_id: String(societyId) }).delete()
  }

  async saveTusdatos(d: TusdatosCredentialsDraft): Promise<void> {
    await this.http.url('accountability/integrations/tusdatos/credentials')
      .body({ society_id: d.societyId, username: d.username, password: d.password })
      .post()
  }

  async disconnectTusdatos(societyId: number): Promise<void> {
    await this.http.url('accountability/integrations/tusdatos/credentials').params({ society_id: String(societyId) }).delete()
  }

  async disconnectTaxCredential(credentialId: number): Promise<void> {
    await this.http.url(`invoice-events/credentials/${credentialId}`).delete()
  }

  async disconnectSii(societyId: number, entity: SiiEntity): Promise<void> {
    await this.http.url('purchases/tax_institution/disconnection').body({ entity, society_id: societyId }).put()
  }

  async dianForwardingInstructions(societyId: number): Promise<string | null> {
    const raw = await this.http.url('invoice-events/dian/forwarding-instructions')
      .params({ society_id: String(societyId) })
      .get<{ forward_to: string | null, sender: string | null }>()
    return raw.forward_to || null
  }

  async alanubeTestSet(societyId: number, governmentId: string): Promise<void> {
    await this.http.url('invoice-events/alanube/test-set').body({ society_id: societyId, government_id: governmentId }).post()
  }

  async connectSii(input: SiiConnectInput): Promise<{ validated: boolean }> {
    const raw = await this.http.url('purchases/sii/credentials/connect').body({
      entity: input.entity,
      rut: input.rut,
      clave: input.clave,
      society_id: input.societyId,
      ...(input.usesPublicInvoicingEntity !== null ? { uses_public_invoicing_entity: input.usesPublicInvoicingEntity } : {}),
    }).post<{ validated?: boolean }>()
    return { validated: raw?.validated !== false }
  }

  async setSiiInvoicingEntity(societyId: number, usesPublicInvoicingEntity: boolean): Promise<void> {
    await this.http.url('purchases/sii/credentials/connect/invoicing-entity')
      .body({ society_id: societyId, uses_public_invoicing_entity: usesPublicInvoicingEntity })
      .post()
  }

  async saveTaxCredential({ credentialId, form }: TaxCredentialUpload): Promise<void> {
    if (credentialId !== null) await this.http.url(`invoice-events/credentials/${credentialId}`).body(form).put()
    else await this.http.url('invoice-events/credentials').body(form).post()
  }

  async saveDianCredential(d: DianCredentialDraft): Promise<void> {
    await this.http.url('invoice-events/dian/credential').body({
      society_id: d.societyId,
      legal_rep_id: d.legalRepId,
      company_nit: d.companyNit,
      id_type: d.legalRepIdType,
    }).post()
  }

  async dianLegalRep(credentialId: number): Promise<DianLegalRep> {
    const raw = await this.http.url(`invoice-events/credentials/${credentialId}`)
      .get<{ legal_rep_id?: string | null, legal_rep_id_type?: string | null }>()
    return { legalRepId: raw.legal_rep_id || null, legalRepIdType: raw.legal_rep_id_type || null }
  }

  async registerAlanube(d: AlanubeRegistrationDraft): Promise<void> {
    await this.http.url('invoice-events/alanube/register').body({
      society_id: d.societyId,
      name: d.name,
      trade_name: d.tradeName || undefined,
      identification: d.identification,
      dv: d.dv || undefined,
      organization_type: d.organizationType,
      identification_type: d.identificationType,
      regime_code: d.regimeCodes.join(';'),
      tax_code: d.taxCode,
      economic_activities: d.economicActivities,
      email: d.email,
      phone: d.phone,
      address: { address: d.address.line, department: d.address.department, city: d.address.city },
    }).post()
  }
}
